import { IAddressFieldsInput, IAddressFieldsByPhoneOutput, IAreaDetailInput } from "@types";
import { userApi } from "gate";
import { useTranslations } from "next-intl";
import { Formik, Form, FormikHelpers } from "formik";
import * as Yup from "yup";
import TextInput from "@elements/fields/textInput";
import Button from "@elements/button";
import parsePhoneNumber from "libphonenumber-js";
import PhoneInput from "@elements/fields/phoneInput";
import { useRouter } from "next/router";
import AreaField from "@elements/fields/areaField";
import { useAppContext } from "@context/app";
import { useState } from "react";

interface SendWithoutAddressProps {
  id?: number;
  onClose?: () => void;
  data?: IAddressFieldsInput;
  action?: (data: IAddressFieldsInput) => void;
}

const SendWithoutAddress: React.FC<SendWithoutAddressProps> = ({ data, id, onClose, action }) => {
  const { locale } = useRouter();
  const t = useTranslations();
  const { country } = useAppContext();
  const [selectedArea, setSelectedArea] = useState<IAreaDetailInput | undefined>();
  const receiverValues = { recipientName: "", recipientNumber: "" };
  const ReceiverSchema = Yup.object().shape({
    recipientName: Yup.string()
      .required(t("client_errors_fieldisrequiered"))
      .min(3, t("client_errors_minchar").replace("@", "3")),
    recipientNumber: Yup.string()
      .required(t("client_errors_fieldisrequiered"))
      .test("test", t("client_errors_phonenotvalid"), (value) => {
        const phoneNumber = parsePhoneNumber(value || "");
        if (phoneNumber) {
          if (phoneNumber.isValid()) {
            return true;
          } else {
            return false;
          }
        } else {
          return false;
        }
      }),
  });

  const handelSubmit = async (
    value: typeof receiverValues,
    actions: FormikHelpers<typeof receiverValues>
  ) => {
    if (!selectedArea) {
      actions.setSubmitting(false);
      return;
    }
    try {
      const res = await userApi
        .addAddressByPhone({
          recipientName: value.recipientName,
          recipientNumber: value.recipientNumber,
          areaId: selectedArea.id,
        })
        .fetch();
      action?.(res.data);
      actions.setSubmitting(false);
      onClose?.();
    } catch (error) {
      onClose?.();
    }
  };

  return (
    <div className=" w-full max-w-[370px]">
      <div className=" w-full flex flex-wrap mb-5 items-center">
        <p className="flex-1 font-normal text-xl text-[#333] text-center mt-1">
          {t("client_address_sendwithoutaddress")}
        </p>
      </div>
      <div
        className="m-auto bg-[#FFF7DF] rounded-2xl p-4 mt-10"
        dir={locale?.includes("ar-") ? "rtl" : "ltr"}
      >
        <p className="text-base font-normal text-[#7A3A00]  w-full leading-7 text-center">
          {t("client_address_sendwithoutaddress_hint")}
        </p>
      </div>
      <div
        className=" w-full flex justify-center mb-5 mt-6"
        dir={locale?.includes("ar-") ? "rtl" : "ltr"}
      >
        <p className=" text-[#999] font-normal text-sm">
          {t("client_address_sendwithoutaddress_message")}
        </p>
      </div>
      <div className="w-full">
        <Formik
          enableReinitialize
          initialValues={receiverValues}
          validationSchema={() => ReceiverSchema}
          validateOnBlur={false}
          onSubmit={(values, actions) => {
            handelSubmit(values, actions);
          }}
        >
          {({
            isSubmitting,
            isValid,
            dirty,
            handleBlur,
            handleChange,
            values,
            errors,
            touched,
            setFieldValue,
          }) => {
            return (
              <Form className="flex flex-col max-w-[320px] w-full m-auto form ">
                <div className="w-full mb-6">
                  <TextInput
                    error={
                      errors.recipientName && touched.recipientName ? errors.recipientName : ""
                    }
                    value={values.recipientName}
                    name="recipientName"
                    label={t("client_general_name")}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>
                <div className="w-full mb-6">
                  <PhoneInput
                    error={
                      errors.recipientNumber && touched.recipientNumber
                        ? errors.recipientNumber
                        : ""
                    }
                    defaultValue={values.recipientNumber}
                    name="recipientNumber"
                    label={t("client_address_recipientnumber")}
                    onChange={(v) => setFieldValue("recipientNumber", v)}
                    onBlur={handleBlur}
                  />
                </div>
                <div className="w-full mb-6">
                  <AreaField
                    area={selectedArea}
                    onChange={(area) => {
                      setSelectedArea(area);
                    }}
                    placeholder={t("client_area_selectarea")}
                    optionalSubArea={true}
                  />
                  {!selectedArea && (
                    <p className="text-red-500 text-sm mt-1">{t("client_errors_fieldisrequiered")}</p>
                  )}
                </div>

                <Button
                  className="h-12 shadow-md font-extrabold text-base mb-2 rounded-full !w-[200px] mx-auto"
                  size="slim"
                  type="submit"
                  theme="blue"
                  isDisabled={isSubmitting || !selectedArea}
                  loading={isSubmitting}
                >
                  {t("client_buttons_submit")}
                </Button>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default SendWithoutAddress;
